/**
 * ETF top-holdings card.
 * Shows the top N holdings yfinance exposes (usually 8-10) with allocation %.
 */
export default function EtfHoldingsCard({ etf, loading }) {
  if (loading) {
    return (
      <div
        data-testid="etf-holdings-card"
        className="border border-zinc-800 bg-[#121214] p-6 sm:p-7 text-xs font-mono text-zinc-500"
      >
        Loading holdings…
      </div>
    );
  }
  if (!etf || !etf.is_etf) return null;
  const holdings = etf.top_holdings || [];
  if (!holdings.length) {
    return (
      <div
        data-testid="etf-holdings-card"
        className="border border-zinc-800 bg-[#121214] p-6 sm:p-7"
      >
        <div className="text-label mb-2">Top holdings</div>
        <div className="text-xs text-zinc-500 font-mono">
          Holdings data unavailable for this ETF.
        </div>
      </div>
    );
  }

  // Top weight defines the bar scale so the longest row anchors at 100%
  const maxPct = Math.max(...holdings.map((h) => h.pct));

  return (
    <div
      data-testid="etf-holdings-card"
      className="border border-zinc-800 bg-[#121214] p-6 sm:p-7 flex flex-col gap-4"
    >
      <div className="flex items-center justify-between">
        <div className="text-label">Top {holdings.length} holdings</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Issuer-published
        </div>
      </div>
      <ol className="flex flex-col gap-2.5">
        {holdings.map((h, idx) => {
          const pct = h.pct * 100;
          const barW = Math.max(2, (h.pct / maxPct) * 100);
          return (
            <li
              key={h.symbol || `${idx}-${h.name}`}
              data-testid={`etf-holding-${h.symbol || idx}`}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3"
            >
              <span className="font-mono text-[11px] text-zinc-500 tabular-nums w-5">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="min-w-0">
                <div className="text-sm text-zinc-100 truncate normal-case tracking-normal">
                  {h.name || h.symbol}
                </div>
                <div className="relative mt-1 h-1 bg-zinc-900 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-[#d7b5c9]"
                    style={{ width: `${barW}%` }}
                  />
                </div>
              </span>
              <span className="font-mono text-sm text-zinc-100 tabular-nums">
                {pct.toFixed(2)}%
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        Source: Yahoo Finance · figures may lag the issuer&apos;s daily prospectus
      </p>
    </div>
  );
}
